import React, { useState, useEffect } from 'react';
import { ScrollView, StyleSheet, View, SafeAreaView } from 'react-native';
import moment from 'moment';
import InputForm from '../InputForm';
import RadioGroup from '../RadioGroup';
import PickerSelect from '../PickerSelect';
import SwitchWithLabel from '../SwitchWithLabel';
import TitleForm from '../TitleForm';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import { FORMAT_DATE } from '../../constants/Configs';
import BaseText from '../BaseText';
import BottomButton from '../BottomButton';
import DateInput from '../DateInput';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import useMonths from '../../utils/useTextMonths';
import useGender from '../../utils/useTextGender';

const PassportForm = ({ countryList, profile, onSubmit }) => {
  const { tKey } = useComponentTKey('PassportForm');
  const { t } = useTranslation(i18nNS.me);
  const [allowPassportInfo, setAllowPassportInfo] = useState(false);
  const [gender, setGender] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [redressNumber, setRedressNumber] = useState('');
  const [knownTravelerID, setKnownTravelerID] = useState('');
  const [applicableCountry, setApplicableCountry] = useState('');
  const [passport, setPassport] = useState('');
  const [namePassport, setNamePassport] = useState('');
  const [issuingCountry, setIssuingCountry] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [passport2, setPassport2] = useState('');
  const [namePassport2, setNamePassport2] = useState('');
  const [issuingCountry2, setIssuingCountry2] = useState('');
  const [expirationDate2, setExpirationDate2] = useState('');
  const [loading, setLoading] = useState(false);

  const [date, setDate] = useState(new Date(1598051730000));

  const [errorMessages, setErrorMessages] = useState({});
  const [months] = useMonths();
  const [genderList] = useGender(false);
  useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      setAllowPassportInfo(profile.AllowPassportInfoCompanyAdmin);
      setGender(profile.Gender);
      if (profile.BirthDate) {
        const _bir = new Date(profile.BirthDate);
        setBirthDate(_bir);
      }
      setRedressNumber(profile.TSARedressNum);
      setKnownTravelerID(profile.TSAKnownTravelerNum);
      setApplicableCountry(profile.TSAKnownTravelerCountry);
      setPassport(profile.PassportNum1);
      setNamePassport(profile.NameOnPassport);
      setIssuingCountry(profile.PassportCountry1);
      if (profile.PassportExpiry1) {
        const _exp = new Date(profile.PassportExpiry1);
        setExpirationDate(_exp);
      }

      setPassport2(profile.PassportNum2);
      setNamePassport2(profile.NameOnPassport2);
      setIssuingCountry2(profile.PassportCountry2);
      if (profile.PassportExpiry2) {
        const _exp = new Date(profile.PassportExpiry2);
        setExpirationDate2(_exp);
      }
    }
  }, [profile]);

  const onDobChange = (selectedDate, type) => {
    let currentDate = selectedDate || date;
    setDate(currentDate);
    currentDate = moment(currentDate).format(FORMAT_DATE);
    if (type === 'birth') {
      setBirthDate(currentDate);
    } else if (type === 'expiry1') {
      setExpirationDate(currentDate);
    } else if (type === 'expiry2') {
      setExpirationDate2(currentDate);
    }
  };

  const isValid = () => {
    const today = new Date();
    const _errorMessages = {};

    if (!gender) {
      _errorMessages.gender = t('common:Error.requiredGender');
    }
    if (!birthDate || birthDate === '0001-01-01T00:00:00') {
      _errorMessages.birthDate = t('common:Error.requiredBirthDate');
    } else {
      if (!moment(birthDate, FORMAT_DATE).isValid()) {
        _errorMessages.birthDate = t('common:Error.requiredBirthDate');
      }
    }
    if (knownTravelerID && !applicableCountry) {
      _errorMessages.applicableCountry = t('common:Error.requiredApplicableCountry');
    }
    if (!knownTravelerID && applicableCountry) {
      _errorMessages.knownTravelerID = t('common:Error.requiredknownTravelerID');
    }
    if (!passport) {
      _errorMessages.passport = t('common:Error.requiredPassportNumber');
    }
    if (!namePassport) {
      _errorMessages.namePassport = t('common:Error.requiredPassportName');
    }

    if (issuingCountry === 'Select A Country') {
      _errorMessages.issuingCountry = t('common:Error.requiredCountry');
    }
    if (!expirationDate) {
      _errorMessages.expirationDate = t('common:Error.requiredDate');
    } else {
      const _expirationDate = moment(expirationDate);

      if (!_expirationDate.isValid()) {
        _errorMessages.expirationDate = t('common:Error.invalidDate');
      } else if (_expirationDate.isBefore(today)) {
        _errorMessages.expirationDate = t('common:Error.oldDate');
      }
    }

    if (passport2) {
      if (!namePassport2) {
        _errorMessages.namePassport2 = t('common:Error.requiredPassportName');
      }
      if (issuingCountry2 === 'Select A Country') {
        _errorMessages.issuingCountry2 = t('common:Error.requiredCountry');
      }
      if (!expirationDate2) {
        _errorMessages.expirationDate2 = t('common:Error.requiredDate');
      } else {
        const _expirationDate = moment(expirationDate2);

        if (!_expirationDate.isValid()) {
          _errorMessages.expirationDate2 = t('common:Error.invalidDate');
        } else if (_expirationDate.isBefore(today)) {
          _errorMessages.expirationDate2 = t('common:Error.oldDate');
        }
      }
    }

    setErrorMessages(_errorMessages);

    return Object.keys(_errorMessages).length === 0;
  };

  const onPressSubmit = () => {
    setLoading(true);

    if (!isValid()) {
      setLoading(false);
      return;
    }

    onSubmit({
      ...profile,
      AllowPassportInfoCompanyAdmin: allowPassportInfo,
      Gender: gender,
      BirthDate: birthDate,
      TSARedressNum: redressNumber,
      TSAKnownTravelerNum: knownTravelerID,
      TSAKnownTravelerCountry: applicableCountry,
      PassportNum1: passport,
      NameOnPassport: namePassport,
      PassportCountry1: issuingCountry,
      PassportExpiry1: expirationDate,
      PassportNum2: passport2,
      NameOnPassport2: namePassport2,
      PassportCountry2: issuingCountry2,
      PassportExpiry2: expirationDate2,
    });
  };

  return (
    <SafeAreaView style={CommonStyles.flex1}>
      <ScrollView>
        <View style={styles.body}>
          <SwitchWithLabel
            value={allowPassportInfo}
            onChange={() => {
              setAllowPassportInfo(!allowPassportInfo);
            }}
            label={t(tKey('EditPermission'))}
          />
          <BaseText>{t(tKey('Gender'))}</BaseText>
          <RadioGroup
            style={CommonStyles.py3}
            items={genderList}
            onChange={(value) => {
              setGender(value);
            }}
            selected={gender}
          />
          <DateInput
            date={birthDate}
            errorMessage={errorMessages.birthDate}
            isEditModeEnabled={true}
            onDateChange={(value) => {
              onDobChange(value, 'birth');
            }}
            label={t(tKey('DOB'))}
            months={months}
            labelMonth={t(tKey('Month'))}
          />
          <InputForm
            label={t(tKey('RedressNumber'))}
            value={redressNumber}
            maxLength={20}
            onChangeText={setRedressNumber}
          />
          <InputForm
            label={t(tKey('KnownTraveler'))}
            value={knownTravelerID}
            errorMessage={errorMessages.knownTravelerID}
            maxLength={20}
            onChangeText={setKnownTravelerID}
          />
          <PickerSelect
            label={t(tKey('ApplicableCountry'))}
            onValueChange={(value) => {
              setApplicableCountry(value);
            }}
            items={countryList}
            value={applicableCountry}
            errorMessage={errorMessages.knownTravelerID}
            placeholder={{ label: t(tKey('SelectCountry')) }}
          />
          <TitleForm title={t(tKey('PassportInformation'))} />
          <InputForm
            label={t(tKey('Passport#'))}
            value={passport}
            required={true}
            errorMessage={errorMessages.passport}
            onChangeText={setPassport}
          />
          <InputForm
            label={t(tKey('NameOnPassport'))}
            value={namePassport}
            required={true}
            errorMessage={errorMessages.namePassport}
            onChangeText={setNamePassport}
          />
          <PickerSelect
            label={t(tKey('IssuingCountry'))}
            onValueChange={(value) => {
              setIssuingCountry(value);
            }}
            items={countryList}
            value={issuingCountry}
            errorMessage={errorMessages.issuingCountry}
            required={true}
            placeholder={{ label: t(tKey('SelectCountry')) }}
          />
          <View style={styles.expirationDateSection}>
            <DateInput
              date={expirationDate}
              isEditModeEnabled={true}
              onDateChange={(value) => {
                onDobChange(value, 'expiry1');
              }}
              label={t(tKey('ExpirationDate'))}
              months={months}
              labelMonth={t(tKey('Month'))}
            />
          </View>
          <TitleForm />
          <InputForm
            label={t(tKey('Passport#'))}
            value={passport2}
            required={true}
            onChangeText={setPassport2}
          />
          <InputForm
            label={t(tKey('NameOnPassport'))}
            value={namePassport2}
            required={true}
            errorMessage={errorMessages.namePassport2}
            onChangeText={setNamePassport2}
          />
          <PickerSelect
            label={t(tKey('IssuingCountry'))}
            onValueChange={(value) => {
              setIssuingCountry2(value);
            }}
            items={countryList}
            value={issuingCountry2}
            errorMessage={errorMessages.issuingCountry2}
            required={true}
            placeholder={{ label: t(tKey('SelectCountry')) }}
          />
          <View style={styles.expirationDateSection}>
            <DateInput
              date={expirationDate2}
              isEditModeEnabled={true}
              onDateChange={(value) => {
                onDobChange(value, 'expiry2');
              }}
              label={t(tKey('ExpirationDate'))}
              months={months}
              labelMonth={t(tKey('Month'))}
            />
          </View>
        </View>
      </ScrollView>
      <BottomButton loading={loading} onPress={onPressSubmit} label={t(tKey('UpdatePassport'))} />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    backgroundColor: Color.primary,
    ...CommonProperties.p3,
  },
  buttonText: {
    color: 'white',
  },
  body: {
    ...CommonProperties.p3,
  },
  radioGroup: {
    ...CommonProperties.py3,
  },
  expirationDateSection: {
    marginTop: 20,
  },
});

export default PassportForm;
